import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MoreHorizontal, AlertCircle } from 'lucide-react';
import { useGetOngoingProjects } from '../../../services/admin.stats.service';

const OngoingProjectsTable = () => {
  const [animatedProgress, setAnimatedProgress] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const { data: projects, isLoading } = useGetOngoingProjects();

  const processedProjects = useMemo(() => {
    if (!projects) return [];
    
    const now = new Date();
    return projects.map(proj => {
      const start = new Date(proj.createdAt);
      const end = proj.deliveryDate ? new Date(proj.deliveryDate) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000); // Fallback to 7 days if no delivery date
      
      const total = end.getTime() - start.getTime();
      const elapsed = now.getTime() - start.getTime();
      
      let progress = 0;
      if (total > 0) {
        progress = Math.min(Math.max((elapsed / total) * 100, 5), 98); // Min 5% for visibility, max 98% if not completed
      }
      
      return {
        ...proj,
        calculatedProgress: Math.round(progress),
        formattedDate: new Date(proj.createdAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })
      };
    });
  }, [projects]);

  // Animate progress bars
  useEffect(() => {
    if (processedProjects.length > 0) {
      setAnimatedProgress(new Array(processedProjects.length).fill(0));
      
      const timeout = setTimeout(() => {
        setAnimatedProgress(processedProjects.map(p => p.calculatedProgress));
      }, 100);
      
      return () => clearTimeout(timeout);
    }
  }, [processedProjects]);

  // Handle dropdown close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-[#1d2026] p-6 rounded-[20px] w-full md:w-[65%] h-[400px] flex items-center justify-center border border-gray-100 dark:border-[#32353c] shadow-sm">
        <div className="w-8 h-8 border-4 border-[#4318FF] dark:border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1d2026] p-6 rounded-[20px] overflow-auto w-full md:w-[65%] border border-gray-100 dark:border-[#32353c] shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#2B3674] dark:text-white">Ongoing projects</h2>

  
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
          <thead className="text-xs text-gray-400 dark:text-gray-500 uppercase border-b border-gray-50 dark:border-[#32353c]">
            <tr>
              <th className="py-3 font-bold">Name</th>
              <th className="py-3 font-bold">Status</th>
              <th className="py-3 font-bold">Date</th>
              <th className="py-3 font-bold">Progress</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
            {processedProjects.map((proj, index) => (
              <tr key={index} className="group hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors">
                <td className="py-2 font-medium text-[#1B2559] dark:text-gray-200 text-[14px] max-w-[200px] truncate">
                  {proj.title}
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-orange-400" />
                    <span className="font-normal text-[13px] text-[#1B2559] dark:text-gray-200">Ongoing</span>
                  </div>
                </td>
                <td className="py-2 text-[13px] text-[#A3AED0] dark:text-gray-400 font-normal">{proj.formattedDate}</td>
                <td className="py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#4318FF] dark:bg-blue-500 transition-all duration-1000 ease-out"
                        style={{ width: `${animatedProgress[index] || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-[12px] font-medium text-[#1B2559] dark:text-gray-200">{animatedProgress[index] || 0}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {processedProjects.length === 0 && (
              <tr>
                <td colSpan="4" className="py-10 text-center text-gray-400 italic font-medium">
                  No ongoing projects found at the moment.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OngoingProjectsTable;
